
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Layout } from "@/components/Layout";
import { ClassCard } from "@/components/ClassCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Filter, Users, Clock, Calendar, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchClasses } from "@/lib/supabase";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { format, parseISO, startOfWeek, endOfWeek, isWithinInterval, addWeeks, startOfMonth, endOfMonth, addMonths } from "date-fns";
import { useTranslation } from "react-i18next";

const Classes = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const { t } = useTranslation();
  
  // The fetchClasses function now automatically filters out past classes
  const { data: classes, isLoading, error } = useQuery({
    queryKey: ['classes'],
    queryFn: fetchClasses,
  });
  
  // Group classes by time periods (first two weeks, then by month)
  const groupedClasses = React.useMemo(() => {
    if (!classes || classes.length === 0) return [];
    
    const today = new Date();
    const periods = [];
    
    // Add first two weeks
    for (let i = 0; i < 2; i++) {
      const weekStart = startOfWeek(addWeeks(today, i), { weekStartsOn: 1 }); // Start on Monday
      const weekEnd = endOfWeek(addWeeks(today, i), { weekStartsOn: 1 });
      
      periods.push({
        label: i === 0 ? t("classes.thisWeek") : t("classes.nextWeek"),
        value: `week-${i}`,
        startDate: weekStart,
        endDate: weekEnd
      });
    }
    
    // Add next 2 months
    for (let i = 0; i < 2; i++) {
      const monthStart = startOfMonth(addMonths(today, i + 1)); // Start with next month
      const monthEnd = endOfMonth(addMonths(today, i + 1));
      const monthName = format(monthStart, 'MMMM'); // Full month name (e.g., "May", "June")
      
      periods.push({
        label: monthName,
        value: `month-${i}`,
        startDate: monthStart,
        endDate: monthEnd
      });
    }
    
    return periods;
  }, [classes, t]);
  
  // Filter classes based on the selected time period
  const filteredClasses = React.useMemo(() => {
    if (!classes) return [];
    if (activeFilter === "all") return classes;
    
    const selectedPeriod = groupedClasses.find(period => period.value === activeFilter);
    if (!selectedPeriod) return [];
    
    return classes.filter(cls => {
      if (!cls.date) return false;
      const classDate = parseISO(cls.date);
      return isWithinInterval(classDate, { 
        start: selectedPeriod.startDate, 
        end: selectedPeriod.endDate 
      });
    });
  }, [classes, activeFilter, groupedClasses]);
  
  return (
    <Layout>
      <section className="py-12 md:py-20 bg-gradient-to-b from-clay-50 to-background">
        <div className="container max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-3 py-1 text-xs font-medium bg-clay-100 text-clay-800 rounded-full mb-4">
              {t("classes.schedule")}
            </span>
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">{t("classes.title")}</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("classes.subtitle")}
            </p>
          </motion.div>
          
          {error ? (
            <Alert variant="destructive" className="mb-10">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("common.error")}</AlertTitle>
              <AlertDescription>
                {t("classes.loadError")}
                <div className="mt-2 text-xs opacity-80">
                  {error instanceof Error ? error.message : t("common.unknownError")}
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="mb-10">
              <Tabs defaultValue="all" onValueChange={setActiveFilter}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                  <div className="flex items-center">
                    <Filter className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{t("classes.filterByWeek")}:</span>
                  </div>
                  <TabsList className="flex flex-wrap">
                    <TabsTrigger value="all">{t("classes.all")}</TabsTrigger>
                    {groupedClasses.map(week => (
                      <TabsTrigger key={week.value} value={week.value}>{week.label}</TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                
                {isLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div key={index} className="border border-border rounded-xl overflow-hidden">
                        <Skeleton className="w-full h-52" />
                        <div className="p-5 space-y-3">
                          <Skeleton className="h-6 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-10 w-full mt-4" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <TabsContent value="all" className="mt-0">
                      <ClassesGrid classes={filteredClasses} />
                    </TabsContent>
                    
                    {groupedClasses.map(week => (
                      <TabsContent key={week.value} value={week.value} className="mt-0">
                        <ClassesGrid classes={filteredClasses} />
                      </TabsContent>
                    ))}
                  </>
                )}
              </Tabs>
            </div>
          )}
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-xl p-6 md:p-8"
          >
            <h2 className="text-2xl font-display font-medium mb-4">{t("classes.information.title")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-clay-100 rounded-full flex items-center justify-center mr-4">
                  <Clock className="h-5 w-5 text-clay-800" />
                </div>
                <div>
                  <h3 className="text-base font-medium mb-2">{t("classes.information.duration.title")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("classes.information.duration.description")}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-clay-100 rounded-full flex items-center justify-center mr-4">
                  <Users className="h-5 w-5 text-clay-800" />
                </div>
                <div>
                  <h3 className="text-base font-medium mb-2">{t("classes.information.size.title")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("classes.information.size.description")}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-clay-100 rounded-full flex items-center justify-center mr-4">
                  <Calendar className="h-5 w-5 text-clay-800" />
                </div>
                <div>
                  <h3 className="text-base font-medium mb-2">{t("classes.information.schedule.title")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("classes.information.schedule.description")}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

// Helper component for the grid of classes
const ClassesGrid: React.FC<{ classes: any[] }> = ({ classes }) => {
  const { t } = useTranslation();
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {classes.length > 0 ? (
        classes.map((classItem, index) => (
          <ClassCard key={classItem.id} classData={classItem} index={index} />
        ))
      ) : (
        <div className="col-span-full text-center py-12">
          <h3 className="text-xl font-medium mb-2">{t("classes.noClasses")}</h3>
          <p className="text-muted-foreground">
            {t("classes.noClassesDesc")}
          </p>
        </div>
      )}
    </div>
  );
};

export default Classes;
